import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axiosRetry from "axios-retry";
import { ApiResponse, ErrorResponse } from "../types/responses/api-response";
import { config as appConfig } from "../config/env";
import { redirectToLoginPreservingLocation } from "../utils/return-url";
import {
  createRequestTimer,
  createResponseTimer,
  createErrorLogger,
} from "../utils/api-logger";
import { ApiError } from "./api-error";
import { SecureStorage } from "../utils/secure-storage";
import {
  cardGateController,
  isTrialCardRequiredError,
} from "./card-gate/card-gate-controller";

/** Задержка перед последним повтором: min(1s·2^(n-1), 30s) из retryDelay ниже. */
const MAX_BACKOFF_SEC =
  Math.min(1000 * Math.pow(2, appConfig.API_RETRY_ATTEMPTS - 1), 30000) / 1000;

/**
 * `Retry-After` отказа в секундах (AD-184). Наверх `httpClient` отдаёт
 * `ApiError`, у которого поля `response` нет, — без этого разбора заголовок до
 * вызывающего кода не доезжает вовсе, и разбор 429 мог только гадать.
 *
 * Только секунды: бэкенд шлёт целое `max(1, ceil(timeToExpire))`
 * (`rate-limit.guard.ts`, `consume`); форма HTTP-date не поддерживается. Имя
 * заголовка — без учёта регистра в обеих формах, которые аксиос отдаёт:
 * `AxiosHeaders` и простой объект. Заголовок, спрятанный браузером (нет в
 * `Access-Control-Expose-Headers`), даёт `undefined`.
 */
export function parseRetryAfterSeconds(
  headers: AxiosResponse["headers"] | undefined,
): number | undefined {
  if (!headers) return undefined;
  const raw: unknown =
    headers instanceof AxiosHeaders
      ? headers.get("retry-after")
      : Object.entries(headers).find(
          ([name]) => name.toLowerCase() === "retry-after",
        )?.[1];
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw >= 0 ? raw : undefined;
  }
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
}

/** Ответ-вложение: сами байты и имя файла, если сервер дал его прочитать. */
export interface BlobDownload {
  blob: Blob;
  /**
   * Имя из `Content-Disposition`. `undefined` — законный случай, а не ошибка:
   * браузер прячет этот заголовок от скрипта, пока сервер не перечислит его в
   * `Access-Control-Expose-Headers` (а API и фронт живут на разных origin).
   */
  filename?: string | undefined;
}

/**
 * Имя файла из `Content-Disposition: attachment; filename="…"`.
 * Заголовки axios приходят как объект с ключами в нижнем регистре; тип у
 * значений широкий, поэтому берём только строку и молча игнорируем остальное —
 * отсутствие имени не повод рушить уже полученный файл.
 */
function parseFilename(headers: AxiosResponse["headers"]): string | undefined {
  const raw = (headers as Record<string, unknown>)["content-disposition"];
  if (typeof raw !== "string") {
    return undefined;
  }
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(raw);
  const name = match?.[1]?.trim();
  return name ? decodeURIComponent(name) : undefined;
}

export class HttpClient {
  private client: AxiosInstance;

  constructor(baseURL: string, config?: AxiosRequestConfig) {
    this.client = axios.create({
      baseURL,
      timeout: appConfig.API_TIMEOUT,
      withCredentials: true, // HttpOnly cookie (refreshToken) автоматически отправляется с каждым запросом
      headers: {
        "Content-Type": "application/json",
      },
      ...config,
    });

    this.setupRetry();
    this.setupInterceptors();
    this.setupTimingInterceptors();
  }

  /**
   * Настроить retry logic для сетевых ошибок
   */
  private setupRetry() {
    axiosRetry(this.client, {
      retries: appConfig.API_RETRY_ATTEMPTS,
      retryDelay: (retryCount) => {
        // Exponential backoff: 1s, 2s, 4s, 8s...
        return Math.min(1000 * Math.pow(2, retryCount - 1), 30000);
      },
      retryCondition: (error) => {
        // Уже нормализованная ошибка не несёт ни response, ни config —
        // isNetworkOrIdempotentRequestError принимает её за обрыв связи и запускает
        // повтор поверх повтора. Каждый лишний виток заново прогонял ошибку через
        // handleError, и настоящая причина («Rate limit exceeded») подменялась на
        // «Ошибка сети. Проверьте подключение к интернету» (прецедент J01, 2026-08-17).
        if (error instanceof ApiError) return false;

        // Не повторять при отказе соединения (бэкенд не запущен) — иначе лавина [HPM] ECONNREFUSED в консоли
        const isConnectionRefused =
          error?.code === "ECONNREFUSED" ||
          error?.message?.includes?.("ECONNREFUSED");
        if (isConnectionRefused) return false;

        if (error.response?.status === 429) {
          // Повтор осмыслен, только если сервер обещает снять лимит раньше, чем
          // выдохнется backoff. Иначе это гарантированно провальные запросы к уже
          // задушенному маршруту — они лишь добивают бюджет следующего окна.
          // axios 1.x: headers — AxiosHeaders, .get() надёжнее чем ["key"].
          const headers = error.response.headers;
          const rawRetryAfter =
            (typeof headers?.get === "function" &&
              headers.get("Retry-After")) ||
            headers?.["retry-after"];
          const retryAfter = Number(rawRetryAfter);
          // Если Retry-After больше нашего max backoff — не повторять, сразу показать ошибку.
          if (Number.isFinite(retryAfter) && retryAfter > MAX_BACKOFF_SEC) {
            return false;
          }
          // Без валидного Retry-After или с коротким окном — не повторять 429 вообще,
          // чтобы не терять response и не превращать "Rate limit" в "Ошибка сети".
          return false;
        }

        return axiosRetry.isNetworkOrIdempotentRequestError(error);
      },
      onRetry: (retryCount, error, requestConfig) => {
        console.warn(
          `🔄 Retry attempt ${retryCount} for ${requestConfig.url}:`,
          error.message,
        );
      },
    });
  }

  /**
   * Настроить timing interceptors для логирования
   */
  private setupTimingInterceptors() {
    this.client.interceptors.request.use(createRequestTimer());
    this.client.interceptors.response.use(
      createResponseTimer(),
      createErrorLogger(),
    );
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      async (config) => {
        const token = await SecureStorage.get("accessToken");

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // КРИТИЧНО: CSRF Protection для всех non-GET запросов (OWASP Top 10)
        if (
          config.method &&
          !["get", "head", "options"].includes(config.method.toLowerCase())
        ) {
          const csrfToken = this.getCsrfToken();
          if (csrfToken) {
            config.headers["X-CSRF-Token"] = csrfToken;
          }
        }

        return config;
      },
      (error) => {
        console.error("HttpClient: Ошибка в request interceptor:", error);
        return Promise.reject(error);
      },
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // ADR-TRIAL-card-gate (Фаза 5): 402 TRIAL_CARD_REQUIRED → открыть card-gate и,
        // после привязки карты, ПРОЗРАЧНО повторить исходный запрос. Единый перехватчик
        // на все точки входа create/publish (Находка 5). Должно идти ДО handleError —
        // нужен сырой AxiosError + config для повтора.
        const cardGateRetry = await this.maybeRetryAfterCardGate(error);
        if (cardGateRetry) return cardGateRetry;

        // При responseType: 'blob' тело ОШИБКИ тоже приезжает Blob'ом, и весь разбор
        // ниже (error.response.data.message, ApiError.details) читает undefined —
        // пользователь получает «Ошибка сети» вместо настоящей причины отказа.
        // Разворачиваем обратно в объект ДО handleError.
        await this.unwrapBlobErrorBody(error);

        // Не логируем ошибки для audit-log endpoint (может не существовать на бекенде)
        const isAuditLogEndpoint = error.config?.url?.includes("/audit-log");
        if (!isAuditLogEndpoint) {
          console.error("HttpClient: Ошибка ответа:", {
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method,
            message: error.response?.data?.message || error.message,
          });
        }

        // КРИТИЧНО: Audit logging для security-relevant errors (SOC 2 compliance)
        // Не логируем 401 на /auth/login (неудачная попытка входа), чтобы не слать запрос на /audit-log и не плодить ошибки в консоли
        const isLoginEndpoint = error.config?.url?.includes("/auth/login");
        // Отказ САМОГО обновления токена разбирает `authApi.refresh`, а не
        // перехватчик: часть таких отказов поправима (проигранная гонка ротации
        // между вкладками, частотный потолок), и стирать сессию по ним нельзя.
        // Итоговое решение принимает бутстрап `AuthContext.loadCurrentUser`:
        // не восстановилось — он сам ставит `isAuthenticated: false`, и на вход
        // уводит `ProtectedRoute`.
        const isRefreshEndpoint =
          error.config?.url?.includes("/auth/refresh") ?? false;
        const isAuditEndpoint = error.config?.url?.includes("/audit-log");
        if (
          !isAuditEndpoint &&
          !isLoginEndpoint &&
          (error.response?.status === 401 || error.response?.status === 403)
        ) {
          try {
            const { logAuditEvent } = await import("../utils/audit-log");
            await logAuditEvent({
              action:
                error.response?.status === 401
                  ? "auth.unauthorized"
                  : "auth.forbidden",
              resource: error.config?.url || "unknown",
              status: "failure",
            });
          } catch (auditError) {
            // Не критично, продолжаем обработку ошибки
          }
        }

        // При 401 очищаем токены и редиректим на логин только если это НЕ запрос самого
        // логина; с публичных страниц хелпер не уводит (бутстрап-401 гостя — штатно),
        // с защищённых — сохраняет место в sessionStorage.returnUrl (SSOT return-url.ts).
        if (
          error.response?.status === 401 &&
          !isLoginEndpoint &&
          !isRefreshEndpoint
        ) {
          try {
            await SecureStorage.remove("accessToken");
            await SecureStorage.remove("refreshToken");
            redirectToLoginPreservingLocation();
          } catch {}
        } else if (error.response?.status === 400) {
          console.error(
            "❌ HttpClient: 400 Bad Request - неверные данные:",
            error.response.data,
          );
        } else if (error.response?.status === 429) {
          console.error(
            "❌ HttpClient: 429 Too Many Requests - превышен лимит запросов",
          );
        } else if (error.response?.status === 500) {
          console.error(
            "❌ HttpClient: 500 Internal Server Error - ошибка сервера",
          );
        } else if (error.code === "ECONNABORTED") {
          console.error("❌ HttpClient: Превышено время ожидания запроса");
        }

        return Promise.reject(this.handleError(error));
      },
    );
  }

  /**
   * Развернуть тело ошибки, приехавшее Blob'ом (следствие responseType: 'blob').
   * Мутирует error.response.data на месте: дальше по цепочке его читают и
   * логгер, и handleError, и вызывающий код — копия не помогла бы.
   */
  private async unwrapBlobErrorBody(error: AxiosError): Promise<void> {
    const body: unknown = error.response?.data;
    if (!(body instanceof Blob)) return;
    // text/html — это не наш конверт ошибки (например, страница пограничника);
    // парсить нечего, оставляем как есть, сообщение соберётся по статусу.
    if (body.type && !body.type.includes("json")) return;
    try {
      const parsed: unknown = JSON.parse(await body.text());
      if (error.response) error.response.data = parsed;
    } catch {
      // тело не JSON — оставляем Blob, handleError уйдёт в сообщение по статусу
    }
  }

  /**
   * Получить CSRF token (Double Submit Cookie Pattern, OWASP Top 10)
   * Приоритет: non-HttpOnly cookie "csrf-token" (устанавливает CsrfMiddleware бекенда)
   * → meta tag → localStorage (legacy fallback)
   */
  private getCsrfToken(): string | null {
    // Primary: читаем из non-HttpOnly cookie, установленной бекендом
    const cookieToken =
      document.cookie
        .split(";")
        .map((c) => c.trim().split("="))
        .find(([n]) => n === "csrf-token")?.[1] ?? null;
    if (cookieToken) return cookieToken;

    // Fallback: meta tag (если бекенд встраивает в HTML)
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]',
    );
    if (meta?.content) return meta.content;

    // Fallback: localStorage (legacy)
    return localStorage.getItem("csrfToken");
  }

  /**
   * ADR-TRIAL-card-gate (Фаза 5). Если ответ — 402 TRIAL_CARD_REQUIRED, открывает
   * card-gate (модалка Stripe Elements SetupIntent → verify), и после привязки карты
   * повторяет исходный запрос РОВНО ОДИН раз (`_cardGateRetried` — защита от цикла,
   * если BE вернёт 402 повторно). Возвращает ответ повтора при успехе; `null` — если
   * это не card-gate или пользователь отменил (ошибка идёт по обычному пути handleError).
   */
  private async maybeRetryAfterCardGate(
    error: AxiosError,
  ): Promise<AxiosResponse | null> {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _cardGateRetried?: boolean })
      | undefined;
    if (!original || original._cardGateRetried) return null;
    if (!isTrialCardRequiredError(error)) return null;

    const cardLinked = await cardGateController.requestCard();
    if (!cardLinked) return null;

    original._cardGateRetried = true;
    return this.client.request(original);
  }

  private handleError(error: unknown): ApiError {
    const axiosError = error as AxiosError<
      Partial<ErrorResponse> & {
        code?: string;
        message?: string;
        details?: Record<string, unknown>;
      }
    >;
    const status = axiosError.response?.status;
    const url = axiosError.config?.url;
    const method = axiosError.config?.method;
    const retryAfterSeconds = parseRetryAfterSeconds(
      axiosError.response?.headers,
    );
    const retryAfter =
      retryAfterSeconds !== undefined ? { retryAfterSeconds } : {};

    if (
      axiosError.response?.data &&
      typeof axiosError.response.data === "object"
    ) {
      const body = axiosError.response.data as Record<string, unknown> & {
        code?: string;
        message?: string | string[];
        details?: Record<string, unknown>;
        error?: string | { code?: string; message?: string; details?: unknown };
      };
      const code =
        (typeof body.code === "string" && body.code) ||
        (status ? `HTTP_${status}` : "UNKNOWN_ERROR");
      const rawMsg = body.message;
      const detailsBlock = body.details as Record<string, unknown> | undefined;
      // ADR-002-конверт ошибок: глобальный exception-filter кладёт причины валидации в
      // body.error.details (массив строк class-validator). Без этой ветки пользователь
      // видит обёртку «Validation failed» вместо реальной причины (прецедент 2026-07-16:
      // смена/сброс пароля). Прежние источники (body.errors / body.details.errors)
      // сохранены для совместимости с другими формами ответов.
      const nestedErrorDetails =
        typeof body.error === "object" && body.error !== null
          ? (body.error as { details?: unknown }).details
          : undefined;
      // ADR-002 в чистом виде: канонический источник сообщения — `error.message`.
      // Верхний `message` бэкенд держит лишь как legacy-дубль: «сохраняются до
      // миграции фронта (W2-S2), затем подлежат удалению»
      // (shared/filters/global-exception.filter.ts). Живая проба прода 2026-08-26
      // подтвердила: сегодня приходят ОБА поля, поэтому ветка ниже стоит ПОСЛЕ
      // legacy-источника и сегодняшнего поведения не меняет ни в одном ответе.
      // В день удаления дубля без неё каждая доменная ошибка превратилась бы
      // в «Произошла неизвестная ошибка» — без причины и без выхода.
      const nestedErrorMessage =
        typeof body.error === "object" &&
        body.error !== null &&
        typeof (body.error as { message?: unknown }).message === "string"
          ? (body.error as { message: string }).message.trim() || undefined
          : undefined;
      const errList =
        body["errors"] ??
        (Array.isArray(detailsBlock?.["errors"])
          ? (detailsBlock["errors"] as unknown[])
          : undefined) ??
        (Array.isArray(nestedErrorDetails) ? nestedErrorDetails : undefined);
      const joinedFieldErrors =
        Array.isArray(errList) && errList.length
          ? errList.filter((e): e is string => typeof e === "string").join("; ")
          : "";
      let message = Array.isArray(rawMsg)
        ? rawMsg.filter((m): m is string => typeof m === "string").join("; ")
        : typeof rawMsg === "string" && rawMsg.trim()
          ? rawMsg
          : typeof body.error === "string"
            ? body.error
            : nestedErrorMessage
              ? nestedErrorMessage
              : "Произошла неизвестная ошибка";
      if (
        joinedFieldErrors &&
        (message === "Validation failed" ||
          message === "Bad Request" ||
          !message.trim())
      ) {
        message = joinedFieldErrors;
      }
      const details: Record<string, unknown> = {
        ...(typeof body.details === "object" && body.details !== null
          ? body.details
          : {}),
        ...retryAfter,
        status,
        url,
        method,
        responseBody: body,
      };

      return new ApiError(message, code, details, status);
    }

    const code = status ? `HTTP_${status}` : "NETWORK_ERROR";
    const isConnectionRefused =
      !status &&
      (axiosError.code === "ERR_NETWORK" ||
        axiosError.code === "ECONNREFUSED" ||
        (axiosError.message && axiosError.message.includes("Network Error")));
    const message = status
      ? `HTTP ${status} при запросе ${method?.toUpperCase?.() || ""} ${url || ""}`.trim()
      : isConnectionRefused
        ? "Не удалось подключиться к серверу. Убедитесь, что бэкенд Event Master запущен (по умолчанию http://localhost:3001)."
        : "Ошибка сети. Проверьте подключение к интернету.";
    const details = { ...retryAfter, status, url, method };

    return new ApiError(message, code, details, status);
  }

  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.get(
      url,
      config,
    );
    return response.data;
  }

  async post<TResponse, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response: AxiosResponse<ApiResponse<TResponse>> =
      await this.client.post(url, data, config);
    return response.data;
  }

  async put<TResponse, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response: AxiosResponse<ApiResponse<TResponse>> =
      await this.client.put(url, data, config);
    return response.data;
  }

  async patch<TResponse, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response: AxiosResponse<ApiResponse<TResponse>> =
      await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(
      url,
      config,
    );
    return response.data;
  }

  /**
   * POST, ответ которого — файл, а не конверт ApiResponse (PDF бейджей, выгрузки).
   * Отдельный метод, потому что `post` типизирован как ApiResponse<T>: пропускать
   * бинарный ответ через него можно только соврав компилятору.
   *
   * `expectedType` — не украшение: до этой правки бинарные ответы забирались голым
   * `fetch(...).then(r => r.blob())` без единой проверки, и при неверном адресе
   * `index.html` уезжал в печать «бейджем». Ответ не того типа — ошибка, а не файл.
   */
  async postBlob<TRequest = unknown>(
    url: string,
    data?: TRequest,
    options?: { expectedType?: string; config?: AxiosRequestConfig },
  ): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.client.post(url, data, {
      ...options?.config,
      responseType: "blob",
    });
    const blob = response.data;
    const expected = options?.expectedType;
    if (expected && !blob.type.includes(expected)) {
      throw new ApiError(
        `Сервер вернул ${blob.type || "ответ без типа"} вместо ${expected}`,
        "UNEXPECTED_CONTENT_TYPE",
        { url, received: blob.type },
        response.status,
      );
    }
    return blob;
  }

  /**
   * GET, ответ которого — файл-вложение, а не конверт ApiResponse (выгрузка
   * участников, отчёты). Отдельный метод по той же причине, что и `postBlob`:
   * `get<T>` типизирован как `ApiResponse<T>` и возвращает `response.data`, то
   * есть на бинарный ответ он смотрит как на конверт. Именно на этом расхождении
   * держалась AD-163: вызывающий объявлял ответ как
   * `ApiResponse<{ downloadUrl: string }>`, бэкенд отдавал байты файла, ссылки в
   * ответе не было никогда — и экран всё равно показывал успех.
   *
   * Возвращается ПАРА, а не голый Blob (в отличие от `postBlob`, чей ответ уходит
   * в печать, а не на диск): у сохраняемого файла должно быть имя, а имя живёт в
   * заголовке `Content-Disposition`. Заголовок необязателен: браузер отдаёт его
   * скрипту только если сервер перечислил его в `Access-Control-Expose-Headers`,
   * поэтому `filename` — `undefined`, а не выдумка, и имя по умолчанию выбирает
   * вызывающий, который знает смысл файла.
   */
  async getBlob(
    url: string,
    options?: { expectedType?: string; config?: AxiosRequestConfig },
  ): Promise<BlobDownload> {
    const response: AxiosResponse<Blob> = await this.client.get(url, {
      ...options?.config,
      responseType: "blob",
    });
    const blob = response.data;
    const expected = options?.expectedType;
    if (expected && !blob.type.includes(expected)) {
      throw new ApiError(
        `Сервер вернул ${blob.type || "ответ без типа"} вместо ${expected}`,
        "UNEXPECTED_CONTENT_TYPE",
        { url, received: blob.type },
        response.status,
      );
    }
    return { blob, filename: parseFilename(response.headers) };
  }
}

// SSOT: единый источник базового URL API — config.API_BASE_URL (REACT_APP_API_BASE_URL).
// Убраны мёртвый Vite-остаток (import.meta.env пуст в CRA-проде) и дубль REACT_APP_API_URL.
const BASE_URL = appConfig.API_BASE_URL;

export const httpClient = new HttpClient(BASE_URL);
